import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';
import { DestroyBase } from '../../../../../../components/common/ng-destroy-base';
import { RaciService } from '../../../../../raci/services/raci.service';
import { LocalizationNavigationService } from '../../../../services/localization-navigation-service';
import { LocalizationService } from '../../../../services/localization-service';
import { reportDesignerWigdets } from '../../../../../report-designer/widgets/widgetsRegistry';
import { FilterOptionEnum } from '../../models/enums/filter-option-enum';
import { GenericPolluxResult } from '../../../../../../models/generic-pollux-result';
import { TranslationStatusEnum } from '../../../../models/enums/translation-status.enum';
import { ReportDesignerWidgetDefinition } from '../../../../../report-designer/models/report-designer-widget-definition';
/**
 * Localization Form List component
 */
@Component({
  selector: 'localization-form-list',
  templateUrl: './localization-form-list.component.html',
  styleUrls: ['./localization-form-list.component.css']
})
export class LocalizationFormListComponent extends DestroyBase implements OnInit {
  /**
   * Selected grid item
   */
  @Input() selectedItem: any;

  /**
  * On widget selection event emitter
  */
  @Output() onWidgetSelection: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Widget mockup data
   */
  widgets: any;

  /**
   * Templates mockup data
   */
  templates: any;

  /**
   * Default template item
   */
  templateDefaultItem: any;

  /**
   * Selected filter option
   */
  selectedFilterOption: FilterOptionEnum = FilterOptionEnum.ShowAllWidgets;

  /**
   * Selected template id
   */
  selectedTemplateOid: number;

  /**
  * Selected language
  */
  selectedLanguage: string;

  /**
  * Filtered widget data based on template selection
  */
  filteredWidgetData: any;

  /**
   * Selected widget data
   */
  selectedWidget: any;

  /**
  * All available widgets
  */
  widgetDefinitions: ReportDesignerWidgetDefinition[] = null;

  /**
 * Selected report template
 */
  @Input() set reportTemplate(value: any) {
    if (value) {
      this.selectedFilterOption = 3;
      this.selectedTemplateOid = value.Oid;
      this.selectedLanguage = value.LanguageId;
    }
  }

  /**
   * Report designer language id
   */
  @Input() reportDesignerLanguageId: any;

  /**
   * Constructor
   * @param _localizationService
   * @param localizationNavigationService
   * @param _raciService
   */
  constructor(private _localizationService: LocalizationService,
    private localizationNavigationService: LocalizationNavigationService,
    private _raciService: RaciService) {
    super();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this._raciService.displayDataLoader(true);
    this.retrieveServiceData();

    this.subscriptions.add(this.subscribeToSaveAction());

    this.templateDefaultItem = {
      "Oid": 0,
      "Name": "Please select template"
    }

    this.subscribeOnTemplateSelected();
  }

  /**
   * Update action status of widget labels
   * @param widgets Collection of widgets
   */
  updateWidgetsActionStatus(widgets: any) {
    widgets?.forEach((widget) => {
      this.changeWidgetState(widget);
    })
  }

  /**
   *
   * Update widget state
   * @param widget widget data
   */
  changeWidgetState(widget: any) {
    const fulfilledLabelValues = widget?.labels?.filter(label => label.editedLanguageValue == null);
    if (fulfilledLabelValues?.length == 0) widget.actionStageId = TranslationStatusEnum.Completed;
    else if (fulfilledLabelValues?.length < widget.labels?.length) widget.actionStageId = TranslationStatusEnum.InProgress;
    else widget.actionStageId = TranslationStatusEnum.Pending;
  }

  /**
   *
   * Subscripiton to Save action
   */
  subscribeToSaveAction() {
    return this.localizationNavigationService.saveClicked$.subscribe(() => {
      this.changeWidgetState(this.selectedWidget);
      const dictionaryId = this.selectedItem ? this.selectedItem.id : this.reportDesignerLanguageId;
      this._localizationService.updateWidget(this.selectedWidget, dictionaryId).subscribe((result: GenericPolluxResult) => {
        if (result.success) {
          this.localizationNavigationService.onIsTranslationUpdated(false);//success => hide save button
        } else {
          alert(result.message);
        }
      });
    });
  }

  /**
   * Action Stage Enum
   */
  public get actionStage(): typeof TranslationStatusEnum {
    return TranslationStatusEnum;
  }

  /**
  * Filter Option Enum
  */
  public get filterOption(): typeof FilterOptionEnum {
    return FilterOptionEnum;
  }

  /**
   * Change Filter option
   * @param option
   */
  changeFilterOption(option: FilterOptionEnum) {
    if (this.selectedFilterOption == option) return;

    this.localizationNavigationService.onTemplateSelected(false);
    this.selectedFilterOption = option;

    if (option === FilterOptionEnum.WidgetsUsed) {
      this.filteredWidgetData = this.widgets.filter(widget => widget.actionStageId == TranslationStatusEnum.InProgress);
      this.selectedTemplateOid = 0;
    } else {
      this.filteredWidgetData = this.widgets;
      this.selectedTemplateOid = 0;
    }
  }

  /**
   * Retrieve data
   */
  private getData() {
    const templatesList = this._localizationService.getTemplates();
    const widgetsList = this._localizationService.getWidgets(this.selectedItem ? this.selectedItem.id : this.reportDesignerLanguageId);

    return forkJoin([templatesList, widgetsList]);
  }

  /**
   * On select widget action
   * @param widgetData Widget data
   */
  onSelectWidget(widgetData: any) {
    this.localizationNavigationService.onIsTranslationUpdated(false);
    this.selectedWidget = widgetData;
    this.onWidgetSelection.emit(widgetData);
    this.widgets.forEach((widget) => { widget.selected = false; });
    widgetData.selected = true;
  }

  /**
   * On select template action
   * @param templateId Selected template id
   */
  onTemplateSelection(templateId: number) {
    const template = this.templates.find(x => x.Oid == templateId);
    if (template) {
      this.selectedItem = this.localizationNavigationService.getActiveLocalization();
      this.filteredWidgetData = this.widgets.filter(widget => template.widgetIds.some(id => id == widget.oid));
      this.localizationNavigationService.setActiveTemplate(template);
      this.localizationNavigationService.onTemplateSelected(true);
    }
  }

  /**
   * Retreves temaplate and widget data from service
   */
  private retrieveServiceData() {
    this.subscriptions.add(this.getData().subscribe(data => {
      this.templates = data[0];
      this.widgets = data[1];
      this.widgetDefinitions = this._localizationService.getWidgetDefinitions();

      this.mapWidgetTitle();

      this.widgets = this.widgets.filter(x => x.title);
      this.updateWidgetsActionStatus(this.widgets);
      this.widgets = this.widgets.sort((a, b) => a.title.localeCompare(b.title));

      if (this.reportDesignerLanguageId) {
        this.onTemplateSelection(this.selectedTemplateOid);
      } else {
        this.filteredWidgetData = this.widgets;
      }

      this.updateWidgetsActionStatus(this.widgets);
      this._raciService.displayDataLoader(false);
    }));
  }

  /**
   * Maps widget title from widgets-definitions.ts
   */
  private mapWidgetTitle() {
    this.widgets.forEach((widget) => {
      let widgetNameComponentEntry = reportDesignerWigdets.find(en => en.dataverseName == widget.name);
      if (!widgetNameComponentEntry) {
        //if not found (many TODOs in reportDesignerWigdets.ts), try by convention (e.g. widget.name = WidgetAppendix => downgradedName should be 'appendixWidget')
        let downgradedWidgetName = widget.name.replace('Widget', '') + 'Widget';
        if (downgradedWidgetName.length > 'widget'.length) {//make sure we have something meaningful
          downgradedWidgetName = downgradedWidgetName[0].toLowerCase() + downgradedWidgetName.slice(1)
          widgetNameComponentEntry = reportDesignerWigdets.find(en => en.downgradedName == downgradedWidgetName);
        }
      }

      if (widgetNameComponentEntry) {
        const widgetSettings = this.widgetDefinitions.find(en => en.downgradedName === widgetNameComponentEntry.downgradedName);
        widget.title = widgetSettings?.config?.title;
        widget.hashtagDisabled = widgetSettings?.config?.disableHashtagDefinition === true;
      }
    });

  }

  /**
   * Subscribe for template selection
   */
  subscribeOnTemplateSelected() {
    this.subscriptions.add(this.localizationNavigationService._templateSelectedSubject.subscribe(value => {
      if (!value) {
        this.resetDataFilters();
      }
    }));
  }

  /**
   * Resets localization list filters and widget data in the list
   */
  private resetDataFilters() {
    this.selectedTemplateOid = 0;
    this.selectedFilterOption = FilterOptionEnum.ShowAllWidgets;
    this.reportDesignerLanguageId = null;
  }
}
